import type { QueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/shared/lib/queryKeys';
import type { Id } from '@/shared/types/domain';

/**
 * Invalidação obrigatória após QUALQUER transição da esteira — submissão,
 * aprovação, solicitação de ajuste, rejeição, conclusão (tech-stack §6).
 *
 * Toda mutation de transição chama esta função, sem exceção. Um projeto
 * aprovado que não aparece no calendário por cache velho quebra a confiança
 * no sistema inteiro, e o usuário não tem como saber que o problema é cache.
 */
export async function invalidateAfterTransition(
  qc: QueryClient,
  projectId: Id,
  ministryId: Id,
): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: queryKeys.projects.detail(projectId) }),
    qc.invalidateQueries({ queryKey: queryKeys.projects.byMinistry(ministryId) }),
    qc.invalidateQueries({ queryKey: queryKeys.projects.pending }),
    // Prefixo `['projects', 'list']` está contido em `['projects']`, mas o
    // alcance amplo é de propósito: qualquer recorte de listagem em cache
    // ficou velho, e uma transição por vez não é caminho quente.
    qc.invalidateQueries({ queryKey: queryKeys.projects.all }),
    // A contagem por situação MUDA em toda transição — é literalmente uma
    // linha saindo de um `status` e entrando em outro. Esquecer daqui produz
    // o pior tipo de erro deste painel: a lista mostra o projeto aprovado e o
    // contador acima dela ainda diz "1 em análise".
    qc.invalidateQueries({ queryKey: queryKeys.projectStatusCounts }),
    qc.invalidateQueries({ queryKey: queryKeys.reviewQueue }),
    // A decisão que a pessoa acabou de tomar aparece no histórico logo abaixo
    // da fila (decisão 28) — sem esta linha ela só apareceria no próximo F5,
    // no lugar exato onde a pessoa vai procurar a confirmação do que fez.
    qc.invalidateQueries({ queryKey: queryKeys.reviewHistory }),
    // Aprovar o projeto A deixa o alerta de conflito do projeto B velho — e é
    // o tipo de bug que só aparece semanas depois (spec-project-review.md §6.4).
    qc.invalidateQueries({ queryKey: ['space-conflicts'] }),
    // Prefixo: alcança todas as combinações de PERÍODO em cache — e a leitura
    // reduzida de um projeto avulso, que mora sob a mesma raiz
    // (`queryKeys.agendaProject`). Filtro deixou de estar na chave
    // (spec-calendar.md decisão 54), então não há mais combinação de filtro
    // para alcançar; o alcance é o mesmo, com menos entradas para invalidar.
    qc.invalidateQueries({ queryKey: ['calendar'] }),
  ]);
}

/**
 * Após salvar rascunho: não mexe na fila — nenhum estado salvável entra nela.
 *
 * ⚠️ **Mexe SIM no calendário, e a versão anterior desta função não mexia.**
 * O docblock antigo dizia "draft não aparece lá", o que era verdade quando foi
 * escrito e deixou de ser na decisão 18 da spec do formulário: `SAVEABLE_STATUSES`
 * inclui `approved` de propósito — *data de evento muda na vida real*. Junte as
 * duas coisas e o líder move o retiro de 14 para 21 de novembro, abre o
 * calendário e o retiro continua no dia 14 — literalmente o cenário que a regra
 * 10 do CLAUDE.md cita para se justificar (spec-calendar.md §3.6).
 */
export async function invalidateAfterDraftSave(
  qc: QueryClient,
  projectId: Id,
): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: queryKeys.projects.detail(projectId) }),
    qc.invalidateQueries({ queryKey: queryKeys.projects.pending }),
    // Incondicional de propósito: salvar um rascunho apenas marca como stale
    // uma query que não está montada — custo zero —, enquanto acertar a
    // condição exigiria o autosave devolver o status do projeto salvo.
    qc.invalidateQueries({ queryKey: ['calendar'] }),
  ]);
}
